import type { Route } from './router';
import type { Prefs } from './prefs';
import type { PlexLibrary } from './plex';

/** Het hoofdmenu: standaardonderdelen plus de eigen volgorde en verborgen onderdelen van het profiel. */

export interface NavEntry {
  id: string;
  label: string;
  href: string;
  active: (r: Route) => boolean;
  /** Mag dit profiel het gebruiken (profielbeheer)? */
  allowed: boolean;
}

export function allNavEntries(libraries: PlexLibrary[], policy: { liveTv: boolean; requests: boolean; music?: boolean; server?: boolean; plex?: boolean } | null): NavEntry[] {
  const video = libraries.filter((l) => l.type === 'movie' || l.type === 'show');
  return [
    { id: 'home', label: 'Home', href: '/', active: (r) => r.name === 'home' || r.name === 'hub', allowed: true },
    ...video.map((l) => ({ id: `lib:${l.key}`, label: l.title, href: `/library/${l.key}`, active: (r: Route) => r.name === 'library' && r.key === l.key, allowed: true })),
    { id: 'mylist', label: 'Mijn lijst', href: '/mylist', active: (r) => r.name === 'mylist', allowed: true },
    { id: 'collections', label: 'Collecties', href: '/collections', active: (r) => r.name === 'collections' || r.name === 'saga' || r.name === 'listView', allowed: policy?.plex !== false },
    { id: 'live', label: 'Live TV', href: '/live', active: (r) => r.name === 'live' || r.name === 'sportLeague', allowed: policy?.liveTv !== false },
    { id: 'sport', label: 'Sport', href: '/sportcenter', active: (r) => r.name === 'sportcenter', allowed: true },
    { id: 'music', label: 'Muziek', href: '/music', active: (r) => r.name === 'music', allowed: policy?.music !== false },
    { id: 'calendar', label: 'Kalender', href: '/calendar', active: (r) => r.name === 'calendar', allowed: true },
    { id: 'server', label: 'Server', href: '/server', active: (r) => r.name === 'server', allowed: policy?.server === true },
    { id: 'requests', label: 'Verzoeken', href: '/requests', active: (r) => r.name === 'requests', allowed: policy?.requests !== false },
  ];
}

/** In de volgorde van het profiel; nieuwe onderdelen (bijv. een nieuwe bibliotheek) komen op hun standaardplek. */
export function orderedNav(entries: NavEntry[], nav: Prefs['nav']): NavEntry[] {
  if (!nav?.order.length) return entries;
  const pos = new Map(nav.order.map((id, i) => [id, i]));
  const known = entries.filter((e) => pos.has(e.id)).sort((a, b) => pos.get(a.id)! - pos.get(b.id)!);
  const out = [...known];
  // Onbekende onderdelen invoegen na hun standaard-voorganger
  entries.forEach((e, i) => {
    if (pos.has(e.id)) return;
    const prev = entries.slice(0, i).reverse().find((x) => out.includes(x));
    out.splice(prev ? out.indexOf(prev) + 1 : 0, 0, e);
  });
  return out;
}

export function visibleNav(entries: NavEntry[], nav: Prefs['nav']): NavEntry[] {
  const hidden = new Set(nav?.hidden ?? []);
  return orderedNav(entries, nav).filter((e) => e.allowed && !hidden.has(e.id));
}
